import 'braid-design-system/reset';
import { ApolloProvider } from '@apollo/client';
import { AnalyticsBrowser } from '@segment/analytics-next';
import {
  BraidProvider,
  makeLinkComponent,
  ToastProvider,
} from 'braid-design-system';
import seekBusiness from 'braid-design-system/themes/seekBusiness';
import { CookiesProvider } from 'react-cookie';
import { Provider } from 'react-redux';
import { Link as ReactRouterLink } from 'react-router-dom';

import Router from 'src/components/App/Router';
import { NavigationHeaderContainer } from 'src/components/NavigationHeaderContainer/NavigationHeaderContainer';
import ScrollToTop from 'src/components/ScrollToTop/ScrollToTop';
import { setEnvironment, type Environment, getConfigValues } from 'src/config';
import { useAuth } from 'src/hooks/auth';
import { legacyApi } from 'src/services/api';
import { client } from 'src/services/apollo';
import store from 'src/store';
import { loadDatadogRUM } from 'src/utils/datadog';

// We can export this instance to share with rest of our codebase.
export const analytics = AnalyticsBrowser.load({
  writeKey: getConfigValues().SEGMENT_CDP_KEY,
});

interface AppProps {
  site: string;
  environment: Environment;
}

const CustomLink = makeLinkComponent(({ href, ...restProps }, ref) =>
  href[0] === '/' ? (
    <ReactRouterLink ref={ref} to={href} {...restProps} />
  ) : (
    <a ref={ref} href={href} {...restProps} />
  ),
);

const App = ({ environment }: AppProps) => {
  // The below code contains side effects. It is normally bad practice to
  // perform side effects during render however it is not strictly forbidden. A
  // similar example of breaking React's rules can be found here:
  // https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops
  setEnvironment(environment);
  loadDatadogRUM(environment);
  const { getAuthToken, getXToken } = useAuth();
  const apolloClient = client(getAuthToken, getXToken);
  legacyApi.defaults.headers.common.Authorization = `Bearer ${getAuthToken()}`;

  return (
    <ApolloProvider client={apolloClient}>
      <BraidProvider theme={seekBusiness} linkComponent={CustomLink}>
        <ToastProvider>
          <CookiesProvider>
            <ScrollToTop />
            <Provider store={store}>
              <NavigationHeaderContainer />
              <Router />
            </Provider>
          </CookiesProvider>
        </ToastProvider>
      </BraidProvider>
    </ApolloProvider>
  );
};

export default App;
